import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonButton, IonContent, IonSpinner, IonSegmentButton, IonLabel, IonSegment, IonSegmentView, IonSegmentContent } from "@ionic/angular/standalone";
import { catchError, finalize, of, switchMap, tap } from 'rxjs';
import { MatIcon } from "@angular/material/icon";
import { SupportService } from '../../../core/services/support.service';
import { ModalContainerComponent } from "../../../shared/components/modal-container/modal-container.component";
import { IBreadcrumbs } from '../../../shared/components/breadcrumbs/breadcrumbs.component';

@Component({
  selector: 'app-support',
  templateUrl: './support.page.html',
  styleUrls: ['./support.page.scss'],
  imports: [IonContent,
    CommonModule,
    MatGridListModule,
    MatFormFieldModule,
    TranslateModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    IonButton,
    IonContent, MatIcon, IonSpinner, IonSegmentButton, IonLabel, ModalContainerComponent, IonSegment, IonSegmentView, IonSegmentContent]
})
export class SupportPage implements OnInit {
  @ViewChild('attachmentInput') attachmentInput?: ElementRef<HTMLInputElement>;

  currentSegment = 'new';
  ticketForm: FormGroup;
  isSubmitting = false;
  isDragging = false;
  selectedFile: File | null = null;
  attachmentError = '';
  public breadcrumbsList: IBreadcrumbs[] = [{
    icon: 'bug_report',
    routerLink: '',
    label: 'SUPPORT'
  }]

  // Esempio dati
  ticketsList: any[] = [];

  constructor(private fb: FormBuilder, private supportService: SupportService) { // Ipotizzo il service
    this.ticketForm = this.fb.group({
      category: ['bug', Validators.required],
      summary: ['', Validators.required],
      description: ['', Validators.required]
    });
    this.supportService.getTicketsList$().subscribe(tickets => {
      this.ticketsList = this.extractTickets(tickets);
    });
  }

  submitTicket() {
    if (this.ticketForm.valid) {
      this.isSubmitting = true;

      // Costruiamo il FormData
      const formData = new FormData();
      formData.append('category', this.ticketForm.get('category')?.value);
      formData.append('priority', 'medium');
      formData.append('summary', this.ticketForm.get('summary')?.value);
      formData.append('description', this.ticketForm.get('description')?.value);

      // Se l'utente ha caricato un file (presumo tu l'abbia salvato in una variabile 'selectedFile')
      if (this.selectedFile) {
        formData.append('file', this.selectedFile, this.selectedFile.name);
      }

      const previousTickets = this.ticketsList;
      const submittedSummary = this.ticketForm.get('summary')?.value;
      let submittedTicket: any;

      this.supportService.createSupportTicket$(formData).pipe(
        tap(createdTicket => {
          submittedTicket = this.buildSubmittedTicket(createdTicket, submittedSummary);
          this.ticketsList = [submittedTicket, ...previousTickets];
          this.currentSegment = 'history';
          this.ticketForm.reset({ category: 'bug' });
          this.selectedFile = null;
          this.attachmentError = '';
          if (this.attachmentInput) {
            this.attachmentInput.nativeElement.value = '';
          }
        }),
        switchMap(() => this.supportService.getTicketsList$().pipe(
          catchError(() => of([]))
        )),
        finalize(() => this.isSubmitting = false)
      ).subscribe(tickets => {
        const serverTickets = this.extractTickets(tickets);
        const currentTickets = serverTickets.length ? serverTickets : previousTickets;
        this.ticketsList = this.containsTicket(currentTickets, submittedTicket)
          ? currentTickets
          : [submittedTicket, ...currentTickets];
      });
    }
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.setAttachment(input.files?.[0] ?? null);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const dropZone = event.currentTarget as HTMLElement;
    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !dropZone.contains(nextTarget)) {
      this.isDragging = false;
    }
  }

  onAttachmentDropped(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.setAttachment(event.dataTransfer?.files?.[0] ?? null);
  }

  private setAttachment(file: File | null): void {
    if (!file) {
      this.selectedFile = null;
      this.attachmentError = '';
      return;
    }

    if (!this.isAllowedAttachment(file)) {
      this.selectedFile = null;
      this.attachmentError = 'Formato non supportato. Seleziona un’immagine, un PDF o un documento Word.';
      if (this.attachmentInput) {
        this.attachmentInput.nativeElement.value = '';
      }
      return;
    }

    this.selectedFile = file;
    this.attachmentError = '';
  }

  private isAllowedAttachment(file: File): boolean {
    if (file.type.startsWith('image/')) {
      return true;
    }

    const allowedMimeTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const fileName = file.name.toLowerCase();

    return allowedMimeTypes.includes(file.type)
      || ['.pdf', '.doc', '.docx'].some(extension => fileName.endsWith(extension));
  }

  private extractTickets(response: any): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.tickets)) {
      return response.tickets;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    return [];
  }

  private buildSubmittedTicket(response: any, summary: string): any {
    const ticket = response?.ticket ?? response?.data?.ticket ?? response?.data ?? response ?? {};
    const status = typeof ticket.status === 'object' ? ticket.status?.name : ticket.status;

    return {
      key: ticket.key ?? ticket.id ?? 'Nuovo ticket',
      summary: ticket.summary ?? summary,
      status: status ?? 'Open',
      created: ticket.created ?? new Date().toISOString()
    };
  }

  private containsTicket(tickets: any[], submittedTicket: any): boolean {
    if (submittedTicket.key !== 'Nuovo ticket') {
      return tickets.some(ticket => ticket.key === submittedTicket.key);
    }

    return tickets.some(ticket => ticket.summary === submittedTicket.summary);
  }

  getStatusColor(status: string): string {
    switch (status.toLowerCase()) {
      case 'done': case 'resolved': return '#10b981'; // Verde
      case 'in progress': return '#3b82f6'; // Blu
      default: return '#f59e0b'; // Arancio (Open)
    }
  }

  ngOnInit(): void {
  }
}
